import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  Check,
} from 'lucide-react'
import { Badge, Button } from '../../components/ui'
import type { BadgeVariant } from '../../components/ui'
import { cn } from '../../lib/cn'
import { formatRelativeTime } from '../../shared/lib/formatRelativeTime'
import { isActionableNotification, type NotificationItem } from './types'

const severityConfig = {
  info: { icon: Info, color: 'text-zinc-500 dark:text-zinc-400', badge: 'gray' as BadgeVariant },
  success: { icon: CircleCheck, color: 'text-green-600 dark:text-green-400', badge: 'green' as BadgeVariant },
  warning: { icon: TriangleAlert, color: 'text-amber-600 dark:text-amber-400', badge: 'yellow' as BadgeVariant },
  critical: { icon: CircleAlert, color: 'text-red-600 dark:text-red-400', badge: 'red' as BadgeVariant },
}

const itemActor = (item: NotificationItem): string | undefined =>
  item.actor_name
  || item.actor?.user_name
  || item.actor?.name
  || item.event?.actor_name
  || item.event?.actor?.user_name
  || item.event?.actor?.name
  || undefined

interface NotificationItemRowProps {
  item: NotificationItem
  compact?: boolean
  selected?: boolean
  selectable?: boolean
  onToggleSelected?: (item: NotificationItem) => void
  onActivate: (item: NotificationItem) => void
  onArchive: (item: NotificationItem) => void
}

export function NotificationItemRow({
  item,
  compact = false,
  selected = false,
  selectable = false,
  onToggleSelected,
  onActivate,
  onArchive,
}: NotificationItemRowProps) {
  const config = severityConfig[item.severity]
  const Icon = config.icon
  const unread = !item.read_at
  const actor = itemActor(item)

  return (
    <div
      className={cn(
        'group relative flex gap-3 border-b border-gray-100 transition-colors last:border-b-0 dark:border-zinc-800',
        compact ? 'px-3 py-3' : 'px-4 py-4',
        unread ? 'bg-indigo-50/45 dark:bg-indigo-950/15' : 'bg-white dark:bg-zinc-900',
        'hover:bg-gray-50 dark:hover:bg-zinc-800/70',
      )}
    >
      {selectable && (
        <label className="mt-1 shrink-0">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelected?.(item)}
            aria-label={`Выбрать уведомление: ${item.title}`}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900"
          />
        </label>
      )}
      <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', unread ? 'bg-indigo-600' : 'bg-transparent')} />
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', config.color)} aria-hidden="true" />
      <button
        type="button"
        className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        onClick={() => onActivate(item)}
      >
        <span className="flex min-w-0 items-start gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-zinc-100" title={item.title}>
            {item.title}
          </span>
          {!compact && <Badge variant={config.badge}>{item.category}</Badge>}
        </span>
        {item.body && (
          <span className="mt-1 block line-clamp-2 text-sm text-gray-600 dark:text-zinc-300">
            {item.body}
          </span>
        )}
        <span className="mt-1.5 flex flex-wrap items-center gap-x-1 text-xs text-gray-500 dark:text-zinc-400">
          {actor && <span>{actor} ·</span>}
          <span>{formatRelativeTime(item.created_at)}</span>
          {item.action_label && <span className="font-medium text-indigo-600 dark:text-indigo-400">· {item.action_label}</span>}
        </span>
      </button>
      {!item.archived_at && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 px-2"
          icon={<Check className="h-4 w-4" />}
          onClick={() => onArchive(item)}
          aria-label={`Готово: ${item.title}`}
          title={isActionableNotification(item) ? 'Готово' : 'В архив'}
        >
          {!compact && <span>Готово</span>}
        </Button>
      )}
    </div>
  )
}
